import React, { useEffect } from 'react'
import { Box, Stack, Icon, Progress, CircularProgress, CircularProgressLabel, Heading, Text, Button } from '@chakra-ui/react'
import { FaInfoCircle } from 'react-icons/fa'
import api from '../../api'
import useStore from '../../store'
import utils from '../../utils/utils'

import PlansImage from '../../images/plans2.png'

export default function Overview () {
  const { user, team, updateTeam } = useStore()
  const teamId = team?._id

  // Refresh team data in case changed since /users/me called.
  useEffect(() => {
    if (!teamId) return
    api.teams.get(teamId, teamData => {
      updateTeam(teamData)
    })
  }, [teamId])

  return (
    <>
      {utils.hasPermission(user, 'team.manage', team?._id)
        ? (
          <>
            <Box display={{ lg: 'flex' }}>
              <Box p={3} mt={5} rounded='md' bg='gray.100' minW='300px'>
                <Heading size='md' mb={5}>General usage</Heading>
                <TeamUsage title='Team seats (users)' count={team.userCount} maxCount={team.maxUsers} />
                <TeamUsage title='Trials' count={team.trialCount} maxCount={team.maxTrials} />
                <TeamUsage title='Participants' count={team.participantCount} maxCount={team.maxParticipants} />
              </Box>

              <Box ml={3} p={3} mt={5} rounded='md' bg='gray.100' flex={1}>
                <Heading size='md' mb={5}>Communication (last 30 days)</Heading>
                <Stack spacing={3} direction={{ base: 'column', md: 'row' }}>
                  <Box p={2} bg='white' rounded='md' shadow='md' minW='200px' maxW='300px'>
                    <Heading size='sm'>Text messages</Heading>
                    <Text fontSize='sm' color='gray.500'>Track your usage against your limits.</Text>
                    {team.monthlySms
                      ? (
                        <Box mt={2} textAlign='center'>
                          <CircularProgress value={team.smsSegments30Days * 100 / team.monthlySms} size='100px' thickness='10px' color='primary.500'>
                            <CircularProgressLabel fontSize={12}>{team.smsSegments30Days} / {team.monthlySms}<br />segments</CircularProgressLabel>
                          </CircularProgress>
                          <Text fontSize='sm' mt={3} color='gray.500'><Icon as={FaInfoCircle} /> {team.smsCount30Days} individual SMS have been sent. Long messages are split into segments.</Text>
                        </Box>)
                      : <Text fontSize='sm' mt={5}>SMS is not enabled for your team. Reach out to us for more information.</Text>}
                  </Box>

                  <Box p={2} bg='white' rounded='md' shadow='md' minW='200px' maxW='300px'>
                    <Heading size='sm'>WhatsApp</Heading>
                    <Text fontSize='sm' color='gray.500'>Track your usage against your limits.</Text>
                    {team.monthlyWhatsapp
                      ? (
                        <Box mt={2} textAlign='center'>
                          <CircularProgress value={team.whatsappCount30Days * 100 / team.monthlyWhatsapp} size='100px' thickness='10px' color='primary.500'>
                            <CircularProgressLabel fontSize={12}>{team.whatsappCount30Days} / {team.monthlyWhatsapp}<br />messages</CircularProgressLabel>
                          </CircularProgress>
                        </Box>)
                      : <Text fontSize='sm' mt={5}>WhatsApp is not enabled for your team. Reach out to us for more information.</Text>}
                  </Box>

                  <Stack spacing={3} flex={1}>
                    <Box p={2} bg='white' rounded='md' shadow='md' minW='200px' maxW='300px'>
                      <Heading size='sm'>Emails</Heading>
                      <Text fontSize='sm' color='gray.500'>Emails are non-metered.</Text>
                      <Text fontSize='sm' fontWeight='bold' mt={3}>{team.email30Days} emails sent</Text>
                    </Box>
                    <Box p={2} bg='white' rounded='md' shadow='md' minW='200px' maxW='300px'>
                      <Heading size='sm'>Push notifications</Heading>
                      <Text fontSize='sm' color='gray.500'>Push notifications are non-metered.</Text>
                      <Text fontSize='sm' fontWeight='bold' mt={3}>{team.push30Days} notifications sent</Text>
                    </Box>

                  </Stack>
                </Stack>
              </Box>
            </Box>

            <Box flex={1} p={3} mt={5} rounded='md' borderWidth='1px' display='flex' alignItems='center'>
              <Box>
                <img src={PlansImage} style={{ maxWidth: '300px' }} />
              </Box>
              <Box ml={2} flex={1}>
                <Heading size='md' mb={3}>About your plan</Heading>
                <Text mb={2}>We will always work with you and your team to build a plan that suits you and to ensure that you're getting the best value.</Text>
                <Text mb={5}>If you've outgrown your current plan (or want to shrink it), please just reach out to us.</Text>
                <Button colorScheme='primary' w='100%' size='sm' variant='outline' as='a' href={`mailto:hello@trialflare.com?subject=Plan%20Change%20(${team?.name})`}>Talk to us about your plan</Button>
              </Box>
            </Box>
          </>)
        : (
          <Box>
            <Heading>Page unavailable</Heading>
            <Text>This page is only viewable by users that manage the team</Text>
          </Box>)}
    </>
  )
}

function TeamUsage ({ title, count, maxCount }) {
  if (!maxCount) return null
  return (
    <Box mb={5}>
      <Heading size='xs'>{title}</Heading>
      <Box display='flex' alignItems='center'>
        <Progress bg='gray.300' flex='1' colorScheme='primary' value={(count || 0) * 100 / maxCount} hasStripe size='md' roumded='md' />
        <Text ml={2} w='70px'>{count || 0} / {maxCount}</Text>
      </Box>
    </Box>
  )
}
